import { useCallback, useEffect, useRef } from 'react';
import sellerAnalyticsService from '../services/sellerAnalyticsService';

/**
 * Hook per gestire l'analytics business dei seller
 */
export const useSellerAnalytics = () => {
  const observerRef = useRef(null);
  const trackedProductsRef = useRef(new Set());

  /**
   * Traccia impression di prodotto quando diventa visibile
   */
  const trackProductImpression = useCallback((productData, source = null) => {
    if (!productData || !productData.id) {
      console.warn('❌ Dati prodotto mancanti per tracking impression');
      return;
    }

    const productKey = `${productData.id}_${productData.sellerId || productData.vendor_handle}`;
    
    // Evita di tracciare lo stesso prodotto più volte nella stessa sessione
    if (trackedProductsRef.current.has(productKey)) {
      return;
    }

    const actualSource = source || sellerAnalyticsService.determineSource();
    
    sellerAnalyticsService.trackProductImpression(productData, actualSource)
      .then(() => {
        trackedProductsRef.current.add(productKey);
      })
      .catch(error => {
        console.error('❌ Errore nel tracking impression:', error);
      });
  }, []);

  /**
   * Traccia click su prodotto
   */
  const trackProductClick = useCallback((productData, source = null) => {
    if (!productData || !productData.id) {
      console.warn('❌ Dati prodotto mancanti per tracking click');
      return;
    }

    const actualSource = source || sellerAnalyticsService.determineSource();
    
    sellerAnalyticsService.trackProductClick(productData, actualSource)
      .catch(error => {
        console.error('❌ Errore nel tracking click:', error);
      });
  }, []);

  /**
   * Traccia visualizzazione pagina prodotto
   */
  const trackProductView = useCallback((productData, source = null) => {
    if (!productData || !productData.id) {
      console.warn('❌ Dati prodotto mancanti per tracking view');
      return;
    }

    const actualSource = source || sellerAnalyticsService.determineSource();
    
    sellerAnalyticsService.trackProductView(productData, actualSource)
      .catch(error => {
        console.error('❌ Errore nel tracking view:', error);
      });
  }, []);

  /**
   * Traccia click verso sito esterno
   */
  const trackExternalClick = useCallback((productData, source = null) => {
    if (!productData || !productData.id) {
      console.warn('❌ Dati prodotto mancanti per tracking external click');
      return;
    }

    const actualSource = source || sellerAnalyticsService.determineSource();
    
    sellerAnalyticsService.trackExternalClick(productData, actualSource)
      .catch(error => {
        console.error('❌ Errore nel tracking external click:', error);
      });
  }, []);

  /**
   * Traccia aggiunta alla wishlist
   */
  const trackWishlistAdd = useCallback((productData, source = null) => {
    if (!productData || !productData.id) {
      console.warn('❌ Dati prodotto mancanti per tracking wishlist');
      return;
    }

    const actualSource = source || sellerAnalyticsService.determineSource();
    
    sellerAnalyticsService.trackWishlistAdd(productData, actualSource)
      .catch(error => {
        console.error('❌ Errore nel tracking wishlist:', error);
      });
  }, []);

  /**
   * Crea un observer per tracciare automaticamente le impression
   */
  const createImpressionObserver = useCallback(() => {
    if (typeof IntersectionObserver === 'undefined') {
      console.warn('❌ IntersectionObserver non supportato');
      return null;
    }

    return new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const productData = entry.target.dataset.product;
            if (productData) {
              try {
                const parsedProduct = JSON.parse(productData);
                trackProductImpression(parsedProduct);
              } catch (error) {
                console.error('❌ Errore nel parsing dati prodotto:', error);
              }
            }
          }
        });
      },
      {
        threshold: 0.3, // Trigger quando almeno il 30% è visibile (più conservativo)
        rootMargin: '20px' // Trigger più vicino al viewport
      }
    );
  }, [trackProductImpression]);

  /**
   * Osserva un elemento prodotto per tracciare impression
   */
  const observeProduct = useCallback((element, productData) => {
    if (!element || !productData) return;

    // Evita di osservare elementi che potrebbero essere usati per infinite scroll
    if (element.classList.contains('product-grid-item') || 
        element.getAttribute('data-infinite-scroll') === 'true') {
      return;
    }

    if (!observerRef.current) {
      observerRef.current = createImpressionObserver();
    }

    // Aggiungi i dati del prodotto come attributo data
    element.dataset.product = JSON.stringify(productData);
    
    // Inizia l'osservazione
    observerRef.current.observe(element);
  }, [createImpressionObserver]);

  /**
   * Smetti di osservare un elemento
   */
  const unobserveProduct = useCallback((element) => {
    if (observerRef.current && element) {
      observerRef.current.unobserve(element);
    }
  }, []);

  /**
   * Ottiene statistiche di un seller
   */
  const getSellerStats = useCallback(async (sellerId, startDate = null, endDate = null) => {
    return await sellerAnalyticsService.getSellerStats(sellerId, startDate, endDate);
  }, []);

  /**
   * Ottiene metriche di performance di un seller
   */
  const getSellerMetrics = useCallback(async (sellerId, startDate = null, endDate = null) => {
    return await sellerAnalyticsService.getSellerMetrics(sellerId, startDate, endDate);
  }, []);

  // Cleanup dell'observer quando il componente si smonta
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, []);

  return {
    trackProductImpression,
    trackProductClick,
    trackProductView,
    trackExternalClick,
    trackWishlistAdd,
    observeProduct,
    unobserveProduct,
    getSellerStats,
    getSellerMetrics
  };
}; 